<template>
  <div
    class="image-template"
    :class="[
      'image-template--' + shape,
      useInitialsFallback ? 'image-template--initials' : 'image-template--image',
    ]"
    :style="wrapperStyle"
  >
    <img
      v-if="showImage"
      class="image-template__img"
      :style="mediaStyle"
      alt=""
      :src="resolvedUrl"
      @error="onImageError"
    />
    <span
      v-else-if="useInitialsFallback"
      class="image-template__initials"
      :style="initialsStyle"
    >{{ initials }}</span>
    <img
      v-else-if="fallbackMediaUrl"
      class="image-template__img"
      :style="mediaStyle"
      alt=""
      :src="fallbackMediaUrl"
    />
  </div>
</template>

<script>
import {
  buildAvatarInitialsStyle,
  resolveAvatarImageUrl,
} from '../helper/avatarInitials';

export default {
  name: 'ImageTemplate',
  props: {
    width: {
      type: String,
      default: '',
    },
    height: {
      type: String,
      default: '',
    },
    size: {
      type: String,
      default: null,
    },
    thumbnail: {
      type: Object,
      default: null,
    },
    image: {
      type: String,
      default: null,
    },
    imgType: {
      type: String,
      default: 'avatar',
    },
    /** Tên hiển thị khi không có ảnh — dùng 2 chữ cái + màu nền. */
    name: {
      type: String,
      default: '',
    },
    shape: {
      type: String,
      default: 'circle',
    },
  },
  data() {
    return {
      imageError: false,
    };
  },
  computed: {
    resolvedUrl() {
      return resolveAvatarImageUrl({
        image: this.image,
        thumbnail: this.thumbnail,
        size: this.size,
      });
    },
    showImage() {
      return !!this.resolvedUrl && !this.imageError;
    },
    useInitialsFallback() {
      if (!this.supportsInitialsFallback) {
        return false;
      }

      return this.imageError || !this.resolvedUrl;
    },
    fallbackMediaUrl() {
      if (this.supportsInitialsFallback || this.showImage || this.useInitialsFallback) {
        return null;
      }

      return '/img/group/group_image_low.jpg';
    },
    supportsInitialsFallback() {
      return this.imgType === 'avatar' || this.imgType === 'group';
    },
    initialsMeta() {
      return buildAvatarInitialsStyle(this.name);
    },
    initials() {
      return this.initialsMeta.initials;
    },
    initialsStyle() {
      return {
        backgroundColor: this.initialsMeta.backgroundColor,
        color: this.initialsMeta.color,
      };
    },
    wrapperStyle() {
      const style = {};
      if (this.width) {
        style.width = this.width;
      }
      if (this.height) {
        style.height = this.height;
      }
      return style;
    },
    mediaStyle() {
      const style = {};
      if (this.width) {
        style.width = this.width;
      }
      if (this.height) {
        style.height = this.height;
      }
      return style;
    },
  },
  watch: {
    resolvedUrl() {
      this.imageError = false;
    },
    image() {
      this.imageError = false;
    },
    thumbnail: {
      deep: true,
      handler() {
        this.imageError = false;
      },
    },
  },
  methods: {
    onImageError() {
      if (this.supportsInitialsFallback) {
        this.imageError = true;
      }
    },
  },
};
</script>

<style lang="scss">
.image-template {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
}

.image-template--circle {
  border-radius: 50%;
  aspect-ratio: 1 / 1;
}

.image-template--rounded {
  border-radius: 8px;
}

.image-template__img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: inherit;
}

.image-template__initials {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border-radius: inherit;
  font-weight: 600;
  font-size: clamp(11px, 38%, 48px);
  line-height: 1;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  user-select: none;
}
</style>
